import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Flag, FlagOff } from "lucide-react";
import { useEffect, useId, useRef, useState, type FormEvent } from "react";
import type {
  AddLineInput,
  UpdateLineInput,
  WholesaleDuplicateCandidate,
  WholesaleOrderDetail,
  WholesaleOrderHeaderPatch,
} from "../../../shared/contracts.js";
import { LIMITS } from "../../../shared/contracts.js";
import type { PartException } from "../../../shared/domain.js";
import {
  fieldErrorsOf,
  invalidateCaseData,
  isStaleWriteError,
  orderMutationErrorMessage,
  STALE_WRITE_MESSAGE,
} from "../../api/caseMutations.js";
import {
  addWholesaleLine,
  setWholesaleLineException,
  updateWholesaleHeader,
  updateWholesaleLine,
  wholesaleDuplicateCandidatesOf,
  wholesaleQueryKeys,
  type UpdateWholesaleHeaderInput,
  type WholesaleMutationResultDto,
} from "../../api/wholesaleQueries.js";
import { Button } from "../../ui/Button.js";
import { ConfirmDialog, Dialog, DialogActions } from "../../ui/Dialog.js";
import { FormField } from "../../ui/FormField.js";
import { DuplicateWarningDialog } from "../cases/order-form/DuplicateWarningDialog.js";
import { focusFirstInvalid, OrderFormSection, reasonError } from "../cases/order-form/OrderForm.js";
import {
  clearChangedLineMarks,
  lineFieldKey,
  MasterEtaField,
  newClientId,
  newLineValues,
  parseWholeNumber,
  PartLinesGrid,
  toEtaInput,
  validateLine,
  withMasterEta,
  withoutBlankTrailingLines,
  type OrderFormLineErrors,
} from "../cases/order-form/PartLinesGrid.js";
import {
  isSavedUnidentifiedLine,
  LineExceptionDialog,
  linePatch,
  lineValuesFromView,
  savedLineId,
  type EditLineValues,
} from "../cases/parts-actions/EditOrderSheet.js";
import { exceptionLabel } from "../cases/parts-actions/VerifyArrivalDialog.js";
import { CustomerPicker } from "./CustomerPicker.js";
import {
  NO_WHOLESALE_ERRORS,
  validateWholesaleHeader,
  WholesaleDetailsFields,
  wholesaleDuplicateLinks,
  wholesaleErrorsFromServer,
  wholesaleHeaderPatch,
  type WholesaleFieldKey,
  type WholesaleFormErrors,
  type WholesaleHeaderValues,
} from "./WholesaleOrderForm.js";

/**
 * The wholesale edit sheet (spec §4.4 "Edit"): the customer, the reference
 * and R&R number (attachable later), "ETA for all parts", the note, and
 * the lines — add, change, flag — the wholesale twin of the Parts edit
 * sheet, on the same rules.
 *
 * The sheet opens on a snapshot of the order and works from it until it
 * closes. Saving turns the difference into the server's commands, in
 * order: one header `PATCH`, one `PATCH` per changed line, one `POST` per
 * new line, each carrying the version the previous answer returned. A
 * step that fails stops the run; what saved stays saved and is folded into
 * the snapshot, so Save again sends only what is still unsaved.
 *
 * - 409 `STALE_WRITE`: the sheet says so and offers Reload latest.
 * - 409 `DUPLICATE_WHOLESALE_ORDER` (the reference or the customer changed
 *   into another order's key inside 30 days): the warning with links; Save
 *   anyway resubmits with the audited override.
 * - 409 `CUSTOMER_INACTIVE` / `ORDER_CANCELLED` and 422: in the server's
 *   words, beside the field they name where they name one.
 *
 * Exceptions are their own audited command through the shared
 * `LineExceptionDialog`, posted to the wholesale route. On an Arrived
 * order, raising a quantity or adding a line needs a correction reason.
 */

export type WholesaleEditValues = WholesaleHeaderValues & { partLines: EditLineValues[] };

export function wholesaleValuesFromDetail(detail: WholesaleOrderDetail): WholesaleEditValues {
  const { order } = detail;
  return {
    customer: order.customer,
    customerReference: order.customerReference ?? "",
    rrNumber: order.rrNumber ?? "",
    masterEta: order.masterEta ?? "",
    orderNote: order.orderNote ?? "",
    partLines: detail.partLines.map(lineValuesFromView),
  };
}

function newEditLine(masterEta = ""): EditLineValues {
  return { ...newLineValues(masterEta), clientId: newClientId(), lineId: null, quantityVerified: 0, exception: null, exceptionReason: null };
}

export type WholesaleSaveStep =
  | Readonly<{ kind: "header"; patch: WholesaleOrderHeaderPatch }>
  | Readonly<{ kind: "line"; clientId: string; lineId: string; patch: UpdateLineInput["patch"] }>
  | Readonly<{ kind: "add"; clientId: string; input: Omit<AddLineInput, "meta"> }>;

export function buildWholesaleSavePlan(baseline: WholesaleEditValues, values: WholesaleEditValues): WholesaleSaveStep[] {
  const steps: WholesaleSaveStep[] = [];
  const header = wholesaleHeaderPatch(baseline, values);
  if (Object.keys(header).length > 0) steps.push({ kind: "header", patch: header });
  for (const line of values.partLines) {
    if (isSavedUnidentifiedLine(line)) continue;
    if (line.lineId === null) {
      steps.push({
        kind: "add",
        clientId: line.clientId,
        input: {
          partNumber: line.partNumber.trim(),
          description: line.description.trim(),
          quantityOrdered: parseWholeNumber(line.quantityOrdered) ?? Number.NaN,
          eta: toEtaInput(line),
        },
      });
      continue;
    }
    const before = baseline.partLines.find((candidate) => candidate.clientId === line.clientId);
    if (!before) continue;
    const patch = linePatch(before, line);
    if (Object.keys(patch).length > 0) steps.push({ kind: "line", clientId: line.clientId, lineId: line.lineId, patch });
  }
  return steps;
}

/** True when a step can take an Arrived order back to incomplete. */
export function wholesaleStepInvalidatesArrival(step: WholesaleSaveStep, baseline: WholesaleEditValues): boolean {
  if (step.kind === "add") return true;
  if (step.kind === "line" && step.patch.quantityOrdered !== undefined) {
    const before = baseline.partLines.find((line) => line.clientId === step.clientId);
    return before !== undefined && step.patch.quantityOrdered > (parseWholeNumber(before.quantityOrdered) ?? 0);
  }
  return false;
}

function applyStep(
  baseline: WholesaleEditValues,
  values: WholesaleEditValues,
  step: WholesaleSaveStep,
): { baseline: WholesaleEditValues; values: WholesaleEditValues } {
  if (step.kind === "header") {
    const { partLines: _lines, ...header } = values;
    return { baseline: { ...baseline, ...header }, values };
  }
  if (step.kind === "line") {
    const current = values.partLines.find((candidate) => candidate.clientId === step.clientId);
    return {
      baseline: {
        ...baseline,
        partLines: baseline.partLines.map((line) =>
          line.clientId === step.clientId && current ? { ...line, ...current, lineId: line.lineId } : line,
        ),
      },
      values,
    };
  }
  const added = values.partLines.find((line) => line.clientId === step.clientId);
  if (!added) return { baseline, values };
  const saved: EditLineValues = { ...added, lineId: savedLineId(added.clientId) };
  return {
    baseline: { ...baseline, partLines: [...baseline.partLines, saved] },
    values: { ...values, partLines: values.partLines.map((line) => (line.clientId === step.clientId ? saved : line)) },
  };
}

export function validateWholesaleEdit(
  values: WholesaleEditValues,
  baseline: WholesaleEditValues,
  status: WholesaleOrderDetail["order"]["status"],
  correctionReason: string,
): { errors: WholesaleFormErrors; correctionError: string | null } {
  const fields = validateWholesaleHeader(values);
  const lines: Record<string, OrderFormLineErrors> = {};
  for (const line of values.partLines) {
    const errors = validateLine(line);
    const quantity = parseWholeNumber(line.quantityOrdered);
    if (!errors.quantityOrdered && quantity !== null && quantity < line.quantityVerified) {
      errors.quantityOrdered = `${line.quantityVerified} already verified. Reverse the verification before ordering fewer.`;
    }
    if (Object.keys(errors).length > 0) lines[line.clientId] = errors;
  }
  const plan = buildWholesaleSavePlan(baseline, values);
  const needsCorrection = status === "arrived" && plan.some((step) => wholesaleStepInvalidatesArrival(step, baseline));
  const correctionError = needsCorrection
    ? (reasonError(correctionReason) ?? null)
    : correctionReason.trim() !== ""
      ? (reasonError(correctionReason) ?? null)
      : null;
  return {
    errors: {
      fields,
      lines,
      isValid: Object.keys(fields).length === 0 && Object.keys(lines).length === 0 && correctionError === null,
    },
    correctionError,
  };
}

// ---------------------------------------------------------------------------
// The sheet
// ---------------------------------------------------------------------------

export interface WholesaleOrderEditSheetProps {
  open: boolean;
  onClose: () => void;
  /** Snapshotted when the sheet opens. */
  detail: WholesaleOrderDetail;
  onSaved: (result: WholesaleMutationResultDto) => void;
  onExceptionChanged: (result: WholesaleMutationResultDto, partNumber: string, exception: PartException | null) => void;
}

export function WholesaleOrderEditSheet(props: WholesaleOrderEditSheetProps) {
  if (!props.open) return null;
  return <WholesaleEditFlow {...props} />;
}

type DuplicateState = Readonly<{ candidates: readonly WholesaleDuplicateCandidate[]; steps: readonly WholesaleSaveStep[] }>;

type RunOutcome =
  | Readonly<{ kind: "done"; result: WholesaleMutationResultDto | null }>
  | Readonly<{ kind: "stale" }>
  | Readonly<{ kind: "duplicate"; candidates: readonly WholesaleDuplicateCandidate[]; steps: readonly WholesaleSaveStep[] }>
  | Readonly<{ kind: "failed" }>;

function WholesaleEditFlow({ onClose, detail, onSaved, onExceptionChanged }: WholesaleOrderEditSheetProps) {
  const queryClient = useQueryClient();
  const idPrefix = useId();
  const correctionId = useId();
  const formRef = useRef<HTMLFormElement>(null);

  const [snapshot] = useState(() => ({
    orderId: detail.order.id,
    number: detail.order.number,
    status: detail.order.status,
    customerName: detail.order.customer.name,
  }));
  const [version, setVersion] = useState(detail.order.version);
  const [baseline, setBaseline] = useState<WholesaleEditValues>(() => wholesaleValuesFromDetail(detail));
  const [values, setValues] = useState<WholesaleEditValues>(() => wholesaleValuesFromDetail(detail));
  const [errors, setErrors] = useState<WholesaleFormErrors>(NO_WHOLESALE_ERRORS);
  const [correctionReason, setCorrectionReason] = useState("");
  const [correctionError, setCorrectionError] = useState<string | null>(null);
  const [formError, setFormError] = useState<string | null>(null);
  const [stale, setStale] = useState(false);
  const [duplicate, setDuplicate] = useState<DuplicateState | null>(null);
  const [overrideError, setOverrideError] = useState<string | null>(null);
  const [confirmDiscard, setConfirmDiscard] = useState(false);
  const [exceptionLine, setExceptionLine] = useState<EditLineValues | null>(null);
  const [focusRequest, setFocusRequest] = useState(0);
  const [sheetAnnouncement, setSheetAnnouncement] = useState("");

  // What Save sends: the form less the untouched rows at the end (the row
  // Enter adds after the last line, spec §4.3), so a blank row neither
  // dirties the sheet nor is refused.
  const submittable: WholesaleEditValues = { ...values, partLines: withoutBlankTrailingLines(values.partLines) };
  const plan = buildWholesaleSavePlan(baseline, submittable);
  const dirty = plan.length > 0;

  const updateHeader = (patch: Partial<WholesaleHeaderValues>) => {
    setValues((current) => ({
      ...current,
      ...patch,
      ...(patch.masterEta !== undefined ? { partLines: withMasterEta(current.partLines, patch.masterEta) } : {}),
    }));
    setErrors((current) => {
      const fields = { ...current.fields };
      let changed = false;
      for (const key of Object.keys(patch) as WholesaleFieldKey[]) {
        if (fields[key]) {
          delete fields[key];
          changed = true;
        }
      }
      return changed ? { ...current, fields } : current;
    });
    if (formError && !stale) setFormError(null);
  };

  const updateLines = (partLines: EditLineValues[]) => {
    const previousLines = values.partLines;
    setValues((current) => ({ ...current, partLines: withMasterEta(partLines, current.masterEta) }));
    setErrors((current) => {
      const lines = clearChangedLineMarks(current.lines, previousLines, partLines);
      return lines === current.lines ? current : { ...current, lines };
    });
  };

  const send = (step: WholesaleSaveStep, at: number, duplicateOverrideReason?: string) => {
    const correction =
      snapshot.status === "arrived" && wholesaleStepInvalidatesArrival(step, baseline) && correctionReason.trim() !== ""
        ? { correctionReason: correctionReason.trim() }
        : {};
    if (step.kind === "header") {
      const input: UpdateWholesaleHeaderInput = {
        patch: step.patch,
        meta: { expectedVersion: at, ...correction },
        ...(duplicateOverrideReason !== undefined ? { duplicateOverrideReason } : {}),
      };
      return updateWholesaleHeader(snapshot.orderId, input);
    }
    if (step.kind === "line") {
      return updateWholesaleLine(snapshot.orderId, step.lineId, { patch: step.patch, meta: { expectedVersion: at, ...correction } });
    }
    return addWholesaleLine(snapshot.orderId, { ...step.input, meta: { expectedVersion: at, ...correction } });
  };

  const showFieldErrors = (step: WholesaleSaveStep, fieldErrors: Readonly<Record<string, readonly string[]>>) => {
    if (step.kind === "header") {
      const mapped = wholesaleErrorsFromServer(fieldErrors, values);
      const correction = fieldErrors["meta.correctionReason"]?.find((text) => text.length > 0) ?? null;
      setErrors({ ...mapped.errors, isValid: false });
      setCorrectionError(correction);
      setFormError(mapped.unmapped.filter((message) => message !== correction).join(" ") || null);
    } else {
      const lines: Record<string, OrderFormLineErrors> = {};
      const unmapped: string[] = [];
      let correction: string | null = null;
      for (const [path, messages] of Object.entries(fieldErrors)) {
        const message = messages.find((text) => text.length > 0) ?? "Check this field.";
        const key = path.startsWith("patch.") ? path.slice("patch.".length) : path;
        if (key === "meta.correctionReason") {
          correction = message;
          continue;
        }
        const lineKey = lineFieldKey(key.split(".")[0]);
        if (lineKey) lines[step.clientId] = { ...lines[step.clientId], [lineKey]: message };
        else unmapped.push(message);
      }
      setErrors({ fields: {}, lines, isValid: false });
      setCorrectionError(correction);
      setFormError(unmapped.length > 0 ? unmapped.join(" ") : null);
    }
    setFocusRequest((n) => n + 1);
  };

  const run = useMutation({
    mutationFn: async (input: { steps: readonly WholesaleSaveStep[]; overrideReason?: string }): Promise<RunOutcome> => {
      let at = version;
      let base = baseline;
      let vals = values;
      let last: WholesaleMutationResultDto | null = null;
      let savedAny = false;
      const steps = [...input.steps];
      for (let index = 0; index < steps.length; index += 1) {
        const step = steps[index]!;
        try {
          const result = await send(step, at, index === 0 ? input.overrideReason : undefined);
          at = result.version;
          last = result;
          savedAny = true;
          const applied = applyStep(base, vals, step);
          base = applied.baseline;
          vals = applied.values;
          setVersion(at);
          setBaseline(base);
          setValues(vals);
        } catch (error) {
          if (savedAny) void invalidateCaseData(queryClient);
          if (isStaleWriteError(error)) {
            setStale(true);
            setFormError(STALE_WRITE_MESSAGE);
            return { kind: "stale" };
          }
          const candidates = wholesaleDuplicateCandidatesOf(error);
          if (candidates && step.kind === "header") return { kind: "duplicate", candidates, steps: steps.slice(index) };
          const fieldErrors = fieldErrorsOf(error);
          if (fieldErrors) showFieldErrors(step, fieldErrors);
          else setFormError(orderMutationErrorMessage(error));
          return { kind: "failed" };
        }
      }
      if (savedAny) await invalidateCaseData(queryClient);
      return { kind: "done", result: last };
    },
    onSuccess: (outcome) => {
      if (outcome.kind === "done") {
        if (outcome.result) onSaved(outcome.result);
        onClose();
        return;
      }
      if (outcome.kind === "duplicate") {
        setDuplicate({ candidates: outcome.candidates, steps: outcome.steps });
        setOverrideError(null);
      }
    },
  });

  const saving = run.isPending;

  const submit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (saving || stale || !dirty) return;
    const validation = validateWholesaleEdit(submittable, baseline, snapshot.status, correctionReason);
    setErrors(validation.errors);
    setCorrectionError(validation.correctionError);
    setFormError(null);
    if (!validation.errors.isValid) {
      setFocusRequest((n) => n + 1);
      return;
    }
    run.mutate({ steps: plan });
  };

  const proceedWithOverride = (reason: string) => {
    if (!duplicate || saving) return;
    setOverrideError(null);
    run.mutate(
      { steps: duplicate.steps, overrideReason: reason },
      {
        onSuccess: (outcome) => {
          if (outcome.kind === "duplicate") {
            setOverrideError("The server still sees a duplicate. Check the reason and try again.");
            return;
          }
          if (outcome.kind !== "done") setDuplicate(null);
        },
      },
    );
  };

  const requestClose = () => {
    if (saving) return;
    if (dirty) {
      setConfirmDiscard(true);
      return;
    }
    onClose();
  };

  const reloadLatest = () => {
    void queryClient.invalidateQueries({ queryKey: wholesaleQueryKeys.detail(snapshot.orderId) });
    onClose();
  };

  useEffect(() => {
    if (focusRequest > 0) focusFirstInvalid(formRef.current);
  }, [focusRequest]);

  const onExceptionDone = (line: EditLineValues, result: WholesaleMutationResultDto, exception: PartException | null, reason: string) => {
    setVersion(result.version);
    const patchLine = (candidate: EditLineValues) =>
      candidate.clientId === line.clientId ? { ...candidate, exception, exceptionReason: exception ? reason : null } : candidate;
    setValues((current) => ({ ...current, partLines: current.partLines.map(patchLine) }));
    setBaseline((current) => ({ ...current, partLines: current.partLines.map(patchLine) }));
    setExceptionLine(null);
    const saved = baseline.partLines.find((candidate) => candidate.clientId === line.clientId);
    const partNumber = saved?.partNumber ?? line.partNumber;
    setSheetAnnouncement(exception ? `${exceptionLabel(exception)} flagged on ${partNumber}.` : `Exception cleared on ${partNumber}.`);
    onExceptionChanged(result, partNumber, exception);
  };

  const renderLineExtras = (line: EditLineValues) => {
    if (line.lineId === null) return null;
    if (isSavedUnidentifiedLine(line)) {
      return (
        <div className="part-line__status">
          <span className="part-line__verified">Saved — reopen the order to edit or flag this line.</span>
        </div>
      );
    }
    const saved = baseline.partLines.find((candidate) => candidate.clientId === line.clientId) ?? line;
    return (
      <div className="part-line__status">
        {line.quantityVerified > 0 ? <span className="part-line__verified">{line.quantityVerified} verified</span> : null}
        {line.exception ? (
          <>
            <span className="inspector__exception" data-exception={line.exception}>
              {exceptionLabel(line.exception)}
              {line.exceptionReason ? ` — ${line.exceptionReason}` : ""}
            </span>
            <Button variant="quiet" icon={<FlagOff />} disabled={saving || stale} onClick={() => setExceptionLine(line)}>
              {"Clear exception "}
              <span className="visually-hidden">on {saved.partNumber}</span>
            </Button>
          </>
        ) : (
          <Button variant="quiet" icon={<Flag />} disabled={saving || stale} onClick={() => setExceptionLine(line)}>
            {"Flag exception "}
            <span className="visually-hidden">on {saved.partNumber}</span>
          </Button>
        )}
      </div>
    );
  };

  return (
    <>
      <Dialog
        open
        onClose={requestClose}
        title="Edit order"
        description={`${snapshot.number} — ${snapshot.customerName}`}
        size="large"
        pending={saving}
      >
        <form ref={formRef} className="order-form order-form--sheet" noValidate onSubmit={submit}>
          <p role="status" aria-live="polite" className={`inspector__announcement${sheetAnnouncement ? "" : " inspector__announcement--empty"}`}>
            {sheetAnnouncement}
          </p>
          {formError ? (
            <div role="alert" className="form-alert order-form__alert">
              <p className="order-form__alert-text">{formError}</p>
              {stale ? (
                <Button variant="secondary" onClick={reloadLatest}>
                  Reload latest
                </Button>
              ) : null}
            </div>
          ) : null}

          <OrderFormSection title="Customer" level={3}>
            <div className="order-form__grid">
              <CustomerPicker
                id={`${idPrefix}-customer`}
                value={values.customer}
                onChange={(customer) => updateHeader({ customer })}
                error={errors.fields.customer ?? null}
                disabled={saving || stale}
              />
            </div>
          </OrderFormSection>

          <OrderFormSection title="Order details" level={3}>
            <WholesaleDetailsFields values={values} errors={errors.fields} onChange={updateHeader} disabled={saving || stale} idPrefix={idPrefix} />
          </OrderFormSection>

          <OrderFormSection
            title="Parts ordered"
            level={3}
            description="Existing lines stay on record — flag one as a cancelled line rather than removing it."
          >
            <MasterEtaField
              value={values.masterEta}
              error={errors.fields.masterEta}
              onChange={(masterEta) => updateHeader({ masterEta })}
              disabled={saving || stale}
              idPrefix={idPrefix}
            />
            <PartLinesGrid<EditLineValues>
              lines={values.partLines}
              errors={errors.lines}
              onChange={updateLines}
              createLine={() => newEditLine(values.masterEta)}
              canRemove={(line) => line.lineId === null}
              minQuantity={(line) => Math.max(LIMITS.quantity.min, line.quantityVerified)}
              lineDisabled={isSavedUnidentifiedLine}
              renderLineExtras={renderLineExtras}
              disabled={saving || stale}
              idPrefix={idPrefix}
            />
          </OrderFormSection>

          {snapshot.status === "arrived" ? (
            <OrderFormSection title="Correction" level={3}>
              <FormField
                id={correctionId}
                label="Correction reason"
                optional
                hint="Needed when this change takes the order out of Arrived — raising a quantity or adding a part. It is kept in the activity history."
                error={correctionError}
                className="order-form__field--wide"
              >
                {(control) => (
                  <textarea
                    {...control}
                    className="text-input textarea-input"
                    data-size="desktop"
                    rows={2}
                    value={correctionReason}
                    disabled={saving || stale}
                    onChange={(event) => {
                      setCorrectionReason(event.target.value);
                      if (correctionError) setCorrectionError(null);
                    }}
                  />
                )}
              </FormField>
            </OrderFormSection>
          ) : null}

          <DialogActions>
            <Button variant="secondary" onClick={requestClose} disabled={saving}>
              Cancel
            </Button>
            <Button type="submit" variant="primary" busy={saving} disabled={!dirty || stale}>
              Save changes
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      <ConfirmDialog
        open={confirmDiscard}
        onClose={() => setConfirmDiscard(false)}
        onConfirm={() => {
          setConfirmDiscard(false);
          onClose();
        }}
        title="Discard unsaved changes?"
        description="Your edits to this order haven't been saved. If you close now, they're lost."
        confirmLabel="Discard changes"
        cancelLabel="Keep editing"
        tone="destructive"
      />

      <DuplicateWarningDialog
        open={duplicate !== null}
        links={wholesaleDuplicateLinks(duplicate?.candidates ?? [])}
        onClose={() => setDuplicate(null)}
        onProceed={proceedWithOverride}
        pending={saving}
        error={overrideError}
        proceedLabel="Save anyway"
      />

      <LineExceptionDialog<WholesaleMutationResultDto>
        open={exceptionLine !== null}
        caseId={snapshot.orderId}
        version={version}
        line={exceptionLine}
        partNumber={
          exceptionLine
            ? (baseline.partLines.find((candidate) => candidate.clientId === exceptionLine.clientId)?.partNumber ?? exceptionLine.partNumber)
            : ""
        }
        endpoint={(lineId, input) => setWholesaleLineException(snapshot.orderId, lineId, input)}
        onClose={() => setExceptionLine(null)}
        onDone={onExceptionDone}
      />
    </>
  );
}
